import { UserId } from '@business/entities/account';
import {
  FileId,
  FileItemsResponse,
  FileResponse,
  FolderId,
  FolderResponse,
  FolderStructureResponse,
  OrgFileShareItem,
  OrgFileShareItemType,
  MediaItemStatus,
} from '@business/entities/file';
import { FormId } from '@business/entities/form';
import { OrgId } from '@business/entities/organization';
import { Endpoint } from '../endpoint';
import { PublicPresentationResponse } from '@business/entities/presentation';
import { PresentationEntryFormData } from '@modules/form/AddPresentationModal/AddPresentationModal';
import { R } from '@core/utils/r';

export function getItems(orgId: OrgId, parentId?: FolderId, isPublic = false, fileType?: string) {
  return Endpoint.get<FileItemsResponse>(`@gc/folder`, { params: R.objectCompact({ orgId, parentId, isPublic, fileType }) });
}

/** Folders **/
export function createFolder(orgId: OrgId, name: string, parentId?: FolderId, isPublic = false) {
  return Endpoint.post<FolderResponse>(`@gc/folder`, { data: { orgId, name, parentId, isPublic } });
}

export function renameFolder(id: FolderId, name: string) {
  return Endpoint.patch<FolderResponse>(`@gc/folder/${id}`, { data: { name } });
}

export function downloadFolder(id: FolderId, email: string) {
  return Endpoint.post<FolderResponse>(`@gc/folder/download/${id}`, { data: { email } });
}

export function deleteFolder(id: FolderId) {
  return Endpoint.delete<void>(`@gc/folder/${id}`);
}

export function FolderVisibility(id: FolderId) {
  return Endpoint.post<FolderResponse>(`@gc/folder/visibility/${id}`);
}

/* Files */
export function renameFile(id: FileId, name: string) {
  return Endpoint.patch<FileResponse>(`@gc/folder/file/${id}`, { data: { name } });
}

export function deleteFile(id: FileId) {
  return Endpoint.delete<void>(`@gc/folder/file/${id}`);
}

export function createDocument(orgId: OrgId, name: string, folderId: FolderId) {
  return Endpoint.post<FileResponse>(`@gc/folder/document`, { data: { orgId, name, folderId } });
}

export function createForm(orgId: OrgId, name: string, folderId: FolderId, form: FormId) {
  return Endpoint.post<FileResponse>(`@gc/folder/form`, { data: { orgId, name, folderId, form } });
}

export function saveFormValues(id: string, values: string) {
  return Endpoint.post<FileResponse>(`@gc/folder/save-values/${id}`, { data: { values } });
}

export function reloadFile(id: string) {
  return Endpoint.get<FileResponse>(`@gc/folder/reload-file/${id}`);
}

export function folderStructure(orgId: OrgId) {
  return Endpoint.get<FolderStructureResponse>(`@gc/folder/folder-structure`, { params: { orgId } });
}

export function move(id: string, type: 'file' | 'folder', destinationId: string) {
  return Endpoint.post<void>(`@gc/folder/move`, { data: { id, type, destinationId } });
}

export function updateOrg(id: string, type: 'file' | 'folder', orgId: OrgId, folderId?: FolderId) {
  return Endpoint.post<void>(`@gc/folder/org`, { data: { id, type, orgId, folderId } });
}

export function search(orgId: OrgId, search: string, parentid?: FolderId, fileType = null) {
  return Endpoint.get<FileItemsResponse>(`@gc/folder/search`, { params: { orgId, search, parentid, fileType } });
}

export function shareForm(data: { email: string }, id: string) {
  return Endpoint.post<FileResponse>(`@gc/form/share/${id}`, { data });
}
export function FileVisibility(id: FileId) {
  return Endpoint.post<FileResponse>(`@gc/folder/file-visibility/${id}`);
}
export function itemShareUsers(id: FileId | FolderId, type: string, userIds: UserId[]) {
  return Endpoint.post<FileResponse | FolderResponse>(`@gc/folder/share-users`, { data: { id, type, userIds } });
}

export function itemPermissions(id: FileId | FolderId, items: OrgFileShareItem[] = [], type: OrgFileShareItemType = OrgFileShareItemType.file) {
  return Endpoint.post<FileResponse | FolderResponse>(`@gc/folder/permission/${id}`, { data: { items, type } });
}

export function changeFolderStatus(id: FolderId, status: MediaItemStatus) {
  return Endpoint.post<FolderResponse>(`@gc/folder/status/${id}`, { data: { status } });
}

export function createPresentationEntry(orgId: string, folderId: string, data: PresentationEntryFormData) {
  return Endpoint.post<FolderResponse>(`@gc/folder/create-presentation-entry/${data.presentationId}`, {
    data: { orgId, folderId, ...data },
  });
}

export function shareWithClient(id: string, type: 'file' | 'folder', isClient: boolean) {
  return Endpoint.post<FileResponse | FolderResponse>(`@gc/folder/share-with-client`, { data: { id, type, isClient } });
}

export function getFile(id: FileId) {
  return Endpoint.get<FileResponse>(`@gc/folder/file/${id}`);
}
